import { useCallback, useEffect, useRef, useState } from "react";

type UseDirectdLookupParams<T> = {
    /** Valor digitado no campo (placa ou CPF), como esta no input. */
    rawValue: string;
    /** Deixa so o que identifica a consulta (ex.: placa em maiusculas sem hifen; CPF so com digitos). */
    normalize: (raw: string) => string;
    /** Formato valido. Cada consulta nova e cobrada, entao nada invalido chega na API. */
    isValid: (normalized: string) => boolean;
    /** false fora da inclusao/edicao. */
    enabled: boolean;
    /** true quando nao precisa consultar (ex.: edicao sem trocar o valor salvo). */
    shouldSkip?: (normalized: string) => boolean;
    fetcher: (normalized: string) => Promise<T>;
    /** Chamado com o retorno da consulta mais recente; sempre a versao mais nova do callback. */
    onResult: (result: T) => void;
    /** Espera depois da ultima tecla antes de consultar sem precisar sair do campo. */
    debounceMs?: number;
};

type ErrorLike = { response?: { data?: { status?: string; reason?: string } } };

/**
 * Consulta a Direct Data a partir de um campo de formulario: dispara no onBlur ([lookupNow]) e tambem alguns
 * instantes depois da ultima tecla (debounce). Garante:
 * - uma consulta por valor: o valor ja consultado so e consultado de novo se mudar, ou se a consulta falhou por rede/API;
 * - resposta de consulta antiga (o usuario editou o campo no meio) e descartada, sem preencher o formulario nem
 *   mexer no indicador;
 * - [loading] sempre volta para false quando a consulta mais recente termina, com sucesso ou erro.
 */
const useDirectdLookup = <T,>({
    rawValue,
    normalize,
    isValid,
    enabled,
    shouldSkip,
    fetcher,
    onResult,
    debounceMs = 800,
}: UseDirectdLookupParams<T>) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<T | null>(null);
    const [error, setError] = useState<string | null>(null);

    const lastLookedUp = useRef<string>("");
    const requestId = useRef<number>(0);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sempre a versao mais nova dos parametros, para o timer e a resposta nao usarem closure velha.
    const latest = useRef({ rawValue, normalize, isValid, enabled, shouldSkip, fetcher, onResult });
    latest.current = { rawValue, normalize, isValid, enabled, shouldSkip, fetcher, onResult };

    const clearTimer = () => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
    };

    const invalidate = useCallback(() => {
        requestId.current += 1;
        lastLookedUp.current = "";
        setLoading(false);
        setResult(null);
        setError(null);
    }, []);

    const run = useCallback(async () => {
        const current = latest.current;
        const value = current.normalize(current.rawValue);

        if (!current.enabled || !current.isValid(value)) return;
        if (value === lastLookedUp.current) return;
        if (current.shouldSkip?.(value)) return;

        lastLookedUp.current = value;
        const id = ++requestId.current;

        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const data = await current.fetcher(value);
            if (id !== requestId.current) return;
            setResult(data);
            latest.current.onResult(data);
        } catch (caught) {
            if (id !== requestId.current) return;
            const { response } = caught as ErrorLike;
            // Sem resposta (rede/timeout) ou Direct Data fora do ar: sair do campo de novo tenta outra vez.
            if (!response || response.data?.status === "BAD_GATEWAY") lastLookedUp.current = "";
            // Integracao sem token configurado: nao atrapalha o cadastro manual.
            if (response?.data?.status !== "SERVICE_UNAVAILABLE") {
                setError(response?.data?.reason || "Não foi possível consultar. Preencha os dados manualmente.");
            }
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, []);

    // Valor mudou: descarta a consulta em andamento e o resultado antigo, e agenda a nova depois do debounce.
    const normalizedValue = normalize(rawValue);
    useEffect(() => {
        clearTimer();
        if (normalizedValue !== lastLookedUp.current) invalidate();
        if (!enabled) return;
        timer.current = setTimeout(run, debounceMs);
        return clearTimer;
    }, [normalizedValue, enabled, debounceMs, invalidate, run]);

    useEffect(() => () => {
        clearTimer();
        requestId.current += 1;
    }, []);

    /** onBlur: consulta na hora, sem esperar o debounce. */
    const lookupNow = useCallback(() => {
        clearTimer();
        run();
    }, [run]);

    return { loading, result, error, lookupNow, reset: invalidate };
};

export default useDirectdLookup;
